import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMeta } from "@/lib/AppContext";
import { api } from "@/lib/api";
import * as Icons from "lucide-react";
import { ConnectionPanel } from "@/components/ConnectionPanel";
import { LogoutButton } from "@/components/AuthGate";
import logoDark from "@/assets/sng-logo-dark.png";

const STATUS_STYLE = {
  complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
  creating: "bg-amber-100 text-amber-800 border-amber-300",
  failed: "bg-rose-100 text-rose-800 border-rose-300",
};

function CreateModal({ onClose, snapshots, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState("fresh");
  const [source, setSource] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const completeSnaps = snapshots.filter((s) => s.status === "complete");

  const submit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    await api.post("/snapshots", { name: name.trim(), description, review_source: mode === "carry" ? source : "fresh" });
    setSubmitting(false);
    onCreated();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="create-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-stone-900">Create new snapshot</h2>
          <button onClick={onClose} data-testid="create-cancel" className="text-stone-400 hover:text-stone-700"><Icons.X className="w-5 h-5" /></button>
        </div>
        <label className="block text-sm text-stone-600 mb-1">Snapshot name</label>
        <input data-testid="create-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Before October 2026 FUB Cleanup"
          className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <label className="block text-sm text-stone-600 mb-1">Description / reason (optional)</label>
        <textarea data-testid="create-description" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Snapshot taken before restructuring automations, Lead Flow, templates, and tags."
          className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm mb-4 h-20 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <div className="space-y-2 mb-4">
          <div className="text-sm text-stone-600">Local review data</div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" checked={mode === "fresh"} onChange={() => setMode("fresh")} data-testid="create-mode-fresh" className="mt-1" />
            <span><b>Start fresh</b> — all assets begin Unreviewed, no notes copied.</span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" checked={mode === "carry"} onChange={() => setMode("carry")} data-testid="create-mode-carry" className="mt-1" />
            <span><b>Carry forward review data</b> — copy only review status + notes from a source snapshot (matched by FUB id).</span>
          </label>
          {mode === "carry" && (
            <select data-testid="create-source" value={source} onChange={(e) => setSource(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm mt-1 bg-white">
              <option value="">Select source snapshot…</option>
              {completeSnaps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4">
          Creating a snapshot performs a fresh <b>read-only</b> capture from Follow Up Boss (GET only). It never changes FUB and never overwrites an existing snapshot.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-300 rounded-lg hover:bg-stone-100">Cancel</button>
          <button onClick={submit} disabled={submitting || !name.trim() || (mode === "carry" && !source)} data-testid="create-submit"
            className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-700 disabled:opacity-50">
            {submitting ? "Starting…" : "Create snapshot"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ snap, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const match = confirm.trim() === (snap.name || "").trim();

  const del = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/snapshots/${snap.id}`, { params: { confirm_name: confirm.trim() } });
      onDeleted();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="delete-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center gap-2 text-rose-700 mb-2">
          <Icons.AlertTriangle className="w-5 h-5" />
          <h2 className="font-display text-xl text-stone-900">Permanently delete snapshot</h2>
        </div>
        <p className="text-sm text-stone-600">
          This permanently removes <b>{snap.name}</b> and all its local catalog data and review notes.
          Captured configuration from other snapshots is unaffected. This is a <b>local</b> action and never touches Follow Up Boss. It cannot be undone.
        </p>
        <label className="block text-sm text-stone-600 mt-4 mb-1">Type the snapshot name to confirm:</label>
        <input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder={snap.name}
          className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400" />
        {err && <div className="text-xs text-rose-600 mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-300 rounded-lg hover:bg-stone-100">Cancel</button>
          <button onClick={del} disabled={!match || busy} data-testid="delete-confirm-btn"
            className="px-4 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-40">
            {busy ? "Deleting…" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SnapshotCard({ s, nav, onArchive, onDelete }) {
  return (
    <div data-testid={`snapshot-card-${s.id}`} className="bg-white border border-stone-200 rounded-xl p-5 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-xl text-stone-900">{s.name}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[s.status] || "bg-stone-200 text-stone-600 border-stone-300"}`}>{s.status}</span>
          {s.archived && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-stone-200 text-stone-600 border-stone-300">archived</span>}
        </div>
        {s.description && <div className="text-sm text-stone-500 mt-1">{s.description}</div>}
        <div className="text-xs text-stone-400 mt-2 flex gap-3 flex-wrap">
          <span>{new Date(s.created_at).toLocaleString()}</span>
          <span>· {(s.total_assets ?? 0).toLocaleString()} assets</span>
          {s.reviews_carried_forward ? <span>· {s.reviews_carried_forward} reviews carried</span> : null}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {s.status === "complete" && (
          <button onClick={() => nav(`/s/${s.id}`)} data-testid={`open-snapshot-${s.id}`}
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 rounded-lg text-sm hover:bg-stone-700">
            Open <Icons.ArrowRight className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => onArchive(s)} data-testid={`archive-snapshot-${s.id}`} title={s.archived ? "Unarchive" : "Archive"}
          className="p-2 border border-stone-300 rounded-lg text-stone-500 hover:bg-stone-100">
          {s.archived ? <Icons.ArchiveRestore className="w-4 h-4" /> : <Icons.Archive className="w-4 h-4" />}
        </button>
        <button onClick={() => onDelete(s)} data-testid={`delete-snapshot-${s.id}`} title="Delete"
          className="p-2 border border-stone-300 rounded-lg text-rose-500 hover:bg-rose-50">
          <Icons.Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function SnapshotsPage() {
  const { refresh } = useMeta();
  const nav = useNavigate();
  const [all, setAll] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const reload = useCallback(() => {
    api.get("/snapshots", { params: { include_archived: true } }).then((r) => setAll(r.data || []));
    refresh && refresh();
  }, [refresh]);
  useEffect(() => { reload(); }, [reload]);

  const active = (all || []).filter((s) => !s.archived);
  const archived = (all || []).filter((s) => s.archived);

  const onArchive = async (s) => { await api.put(`/snapshots/${s.id}/archive`, { archived: !s.archived }); reload(); };

  return (
    <div className="min-h-screen bg-stone-100">
      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <img src={logoDark} alt="S&G" data-testid="home-logo" className="h-9 w-auto mb-4" />
            <div className="font-display text-4xl text-stone-900">FUB Catalog</div>
            <p className="text-stone-500 mt-1">Historical snapshots of your Follow Up Boss configuration.</p>
          </div>
          <div className="flex items-center gap-5">
          <Link to="/account" data-testid="account-link" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Account</Link>
          <LogoutButton className="text-stone-500 hover:text-stone-900" />
          <button onClick={() => setShowCreate(true)} data-testid="create-snapshot-btn"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-lg hover:bg-stone-700">
            <Icons.Plus className="w-4 h-4" /> Create new snapshot
          </button>
          </div>
        </div>

        <ConnectionPanel />

        {all === null ? (
          <div className="text-stone-400 mt-10">Loading…</div>
        ) : active.length === 0 ? (
          <div className="text-stone-400 mt-10">No snapshots yet. Create your first one.</div>
        ) : (
          <div className="grid gap-4 mt-8">
            {active.map((s) => <SnapshotCard key={s.id} s={s} nav={nav} onArchive={onArchive} onDelete={setDelTarget} />)}
          </div>
        )}

        {archived.length > 0 && (
          <div className="mt-10">
            <button onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived"
              className="text-sm text-stone-500 flex items-center gap-2 hover:text-stone-800">
              <Icons.ChevronDown className={`w-4 h-4 transition-transform ${showArchived ? "" : "-rotate-90"}`} />
              Archived snapshots ({archived.length})
            </button>
            {showArchived && (
              <div className="grid gap-4 mt-4 opacity-80">
                {archived.map((s) => <SnapshotCard key={s.id} s={s} nav={nav} onArchive={onArchive} onDelete={setDelTarget} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal snapshots={all || []} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); setTimeout(reload, 1500); }} />
      )}
      {delTarget && (
        <DeleteModal snap={delTarget} onClose={() => setDelTarget(null)}
          onDeleted={() => { setDelTarget(null); reload(); }} />
      )}
    </div>
  );
}
